import { FC, useMemo } from 'react';

import { getWeekdayTitle } from '@/utils/dateUtils';

import useMiniMwdTimescale from '../../state/selectors/useMiniMwdTimescale';

import styles from './StickyDayLabel.module.css';

/**
 * Renders a fixed day-label pinned to the left edge of the mini MWD scroll container.
 * It fades out as the scrolling day label for the next day approaches from the right,
 * creating a seamless handover between the sticky label and the scrolling one.
 */
const StickyDayLabel: FC<{ scrollLeft: number }> = ({ scrollLeft }) => {
  const { scale, tickValuesDays } = useMiniMwdTimescale();

  const { label, opacity } = useMemo(() => {
    // The label text width in px — must roughly match the CSS font-size 18px rendering
    const labelWidth = 160;

    // Find which day the left edge of the viewport is currently inside
    const days = tickValuesDays.filter((d) => scale(d) <= scrollLeft);
    const currentDay = days[days.length - 1];

    if (!currentDay) return { label: null, opacity: 0 };

    const currentDayX = scale(currentDay);

    // Find the next day tick to detect when its scrolling label approaches
    const currentDayIndex = tickValuesDays.indexOf(currentDay);
    const nextDay = tickValuesDays[currentDayIndex + 1];

    let opacity = 1;
    if (nextDay) {
      const nextDayX = scale(nextDay);
      // Start fading when the next day's scrolling label is within [labelWidth] px of the left edge
      const nextDayDistanceFromLeft = nextDayX - scrollLeft;
      if (nextDayDistanceFromLeft < labelWidth) {
        opacity = Math.max(0, nextDayDistanceFromLeft / labelWidth);
      }
    }

    // Also fade in from 0 as current day label scrolls off to the left
    const currentDayDistanceFromLeft = scrollLeft - currentDayX;
    if (currentDayDistanceFromLeft < labelWidth) {
      // Don't show the sticky label until the scrolling one has moved far enough left
      opacity = Math.min(opacity, currentDayDistanceFromLeft / labelWidth);
    }

    return { label: getWeekdayTitle(currentDay), opacity };
  }, [scrollLeft, scale, tickValuesDays]);

  if (!label) return null;

  return (
    <div className={styles.stickyDayLabel} style={{ opacity, transition: 'opacity 60ms ease-out' }}>
      {label}
    </div>
  );
};

export default StickyDayLabel;
